<template>
  <div :class="['workspace-v2', 'workspace-v2--event', 'seller-swap-page', { 'seller-swap-page--embedded': embedded }]">
    <OrganizerWorkspaceTopbar v-if="!embedded" :menu-open="showNav && isMobileViewport" menu-label="Abrir menú de FOMO Swap" :brand-route="swapSellerHomeRoute" @open-menu="showNav = true" />

    <OrganizerWorkspaceNav
      v-if="!embedded"
      class="workspace-v2__nav--mobile"
      :mobile-open="showNav && isMobileViewport"
      :drawer-mode="true"
      :brand-route="swapSellerHomeRoute"
      workspace-eyebrow="FOMO"
      context-eyebrow="FOMO Swap"
      :context-label="sellerNavContextLabel"
      :workspace-items="sellerWorkspaceNavItems"
      :context-items="sellerContextNavItems"
      @close="showNav = false"
    />

    <div class="workspace-v2__shell workspace-v2__shell--event seller-swap-shell">
      <OrganizerWorkspaceNav
        v-if="!embedded"
        class="workspace-v2__nav--desktop"
        :mobile-open="showNav"
        :brand-route="swapSellerHomeRoute"
        workspace-eyebrow="FOMO"
        context-eyebrow="FOMO Swap"
        :context-label="sellerNavContextLabel"
        :workspace-items="sellerWorkspaceNavItems"
        :context-items="sellerContextNavItems"
        @close="showNav = false"
      />

      <main class="workspace-v2__main seller-swap-main">
      <section v-if="activeSection !== 'sell'" class="seller-swap-hero fomo-dashboard-card">
        <div class="seller-swap-hero__copy">
          <span class="workspace-v2__kicker">FOMO Swap / {{ activeSectionConfig.label }}</span>
          <h1>{{ activeSectionConfig.title }}</h1>
          <p>{{ activeSectionConfig.description }}</p>
        </div>
        <FomoButton v-if="activeSection !== 'profile' && profileNeedsCompletion" variant="quiet" :icon="heroActionIcon" :disabled="heroActionDisabled" @click="handleHeroAction">{{ heroActionLabel }}</FomoButton>
      </section>

      <nav v-if="embedded && showEmbeddedMenu" class="seller-swap-embedded-menu" aria-label="Menú de FOMO Swap">
        <button v-for="item in navItems" :key="item.key" type="button" :class="['seller-swap-embedded-menu__item', { 'is-active': item.key === activeSection }]" @click="setEmbeddedSection(item.key)">
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
          <small>{{ item.helper }}</small>
        </button>
      </nav>

      <div v-if="feedback" :role="feedbackType === 'error' ? 'alert' : 'status'" :class="['seller-swap-feedback', feedbackType === 'error' ? 'is-error' : 'is-success']">{{ feedback }}</div>

      <template v-if="activeSection === 'sell' && !loading"><SwapPublicationSuccess v-if="publishedListing" :listing="publishedListing" @sales="viewPublishedListings" @another="publishedListing = null" /><SwapListingWizard v-else :key="wizardKey" :events="events" :profile="profile" :owner-id="sellerOwnerId" :request="swapApi" :request-form="swapApiForm" @profile-saved="profile = $event" @published="onListingPublished" /></template>

      <section v-if="activeSection === 'tickets'" class="seller-swap-panel fomo-dashboard-card">
        <div class="seller-swap-panel__header seller-swap-panel__header--stacked">
          <div>
            <span class="workspace-v2__kicker">Entradas</span>
            <h2>Entradas y ventas</h2>
            <p>Gestiona publicación, entrega y mensajes desde una sola lista.</p>
          </div>
          <FomoButton variant="quiet" type="button" :disabled="loading" @click="loadAll">Actualizar</FomoButton>
        </div>

        <div class="seller-swap-entry-filters" role="tablist" aria-label="Filtrar entradas">
          <button v-for="filter in entryFilters" :key="filter.key" type="button" :class="['seller-swap-entry-filter', { 'is-active': entryFilter === filter.key }]" @click="entryFilter = filter.key">
            <span>{{ filter.label }}</span>
            <strong>{{ filter.count }}</strong>
          </button>
        </div>

        <div v-if="loading && !entryRows.length" class="seller-swap-empty">Cargando entradas...</div>
        <div v-else-if="!entryRows.length" class="seller-swap-empty seller-swap-empty--illustrated">
          <svg viewBox="0 0 112 76" width="112" height="76" fill="none" aria-hidden="true"><rect x="12" y="10" width="84" height="52" rx="10" stroke="currentColor" opacity=".25" transform="rotate(-7 54 36)"/><path d="M24 22h64a8 8 0 0 1 8 8v6a7 7 0 0 0 0 14v6a8 8 0 0 1-8 8H24a8 8 0 0 1-8-8v-6a7 7 0 0 0 0-14v-6a8 8 0 0 1 8-8Z" stroke="currentColor"/><path d="M70 26v34M30 36h25m-25 9h17" stroke="currentColor" stroke-dasharray="3 4"/></svg>
          <h3>Tu próxima venta empieza aquí</h3><p>Publica una entrada y sigue su venta, entrega y cobro desde este panel.</p>
          <FomoButton variant="action" @click="openSellSection">Publicar una entrada</FomoButton>
        </div>
        <div v-else-if="!filteredEntryRows.length" class="seller-swap-empty">No hay entradas en este filtro.</div>
        <div v-else class="seller-swap-entry-table">
          <article v-for="row in filteredEntryRows" :key="row.key" class="seller-swap-entry-row fomo-card">
            <div class="seller-swap-entry-row__main">
              <div class="seller-swap-entry-row__entry">
                <small>{{ row.kind === 'delivery' ? row.orderNumber : row.eventDate }}</small>
                <strong>{{ row.title }}</strong>
                <span>{{ row.subtitle }}</span>
                <em>{{ row.seat }}</em>
              </div>
              <div class="seller-swap-entry-row__status">
                <span :class="['seller-swap-pill', row.primaryStatusClass]">{{ row.primaryStatusLabel }}</span>
                <span :class="['seller-swap-pill', row.secondaryStatusClass]">{{ row.secondaryStatusLabel }}</span>
              </div>
              <div class="seller-swap-entry-row__money">
                <small>{{ row.moneyKicker }}</small>
                <strong>{{ row.amountLabel }}</strong>
                <span>{{ row.moneyHelper }}</span>
              </div>
              <div class="seller-swap-entry-row__actions">
                <SwapShareLink v-if="row.kind === 'listing'" :listing="row.listing" />
                <FomoButton variant="quiet" @click="toggleEntryDetails(row.key)">{{ expandedEntryKey === row.key ? 'Ocultar' : row.primaryActionLabel }}</FomoButton>
                <FomoButton variant="quiet" v-if="row.kind === 'listing' && row.listing.safe_actions && row.listing.safe_actions.can_pause" type="button" :disabled="listingActionKey === `pause-${row.listing.id}`" @click="runListingAction(row.listing, 'pause')">Pausar</FomoButton>
                <FomoButton variant="quiet" v-if="row.kind === 'listing' && row.listing.safe_actions && row.listing.safe_actions.can_resume" type="button" :disabled="listingActionKey === `resume-${row.listing.id}`" @click="runListingAction(row.listing, 'resume')">Reactivar</FomoButton>
              </div>
            </div>

            <div v-if="expandedEntryKey === row.key" class="seller-swap-entry-row__details">
              <template v-if="row.kind === 'listing'">
                <div class="seller-swap-proof-strip fomo-card">
                  <div>
                    <small>Evidencia</small>
                    <strong>{{ proofStatusLabel(row.listing) }}</strong>
                    <span>{{ proofHelper(row.listing) }}</span>
                  </div>
                  <a v-if="row.listing.proof_file_url" :href="row.listing.proof_file_url" target="_blank" rel="noopener">Ver archivo</a>
                  <template v-for="unit in row.listing.units || []" :key="unit.id"><a v-if="unit.ticket_file_url" :href="unit.ticket_file_url" target="_blank" rel="noopener">Ver entrada {{ unit.unit_number }}</a></template>
                </div>

                <div v-if="row.listing.safe_actions && row.listing.safe_actions.can_edit" class="seller-swap-listing-edit">
                  <label>
                    <span>Título</span>
                    <input type="text" :value="listingEditField(row.listing, 'title')" @input="setListingEditField(row.listing, 'title', $event.target.value)" />
                  </label>
                  <label>
                    <span>Ajustar precio USD</span>
                    <input type="number" min="0.01" step="0.01" :value="listingEditField(row.listing, 'unit_price')" @input="setListingEditField(row.listing, 'unit_price', $event.target.value)" />
                  </label>
                  <div v-if="listingSuggestedPrice(row.listing)" class="seller-swap-price-context seller-swap-field--wide">
                    <span>Oficial {{ money(listingOfficialPrice(row.listing), row.listing.currency) }}</span>
                    <strong>Sugerido {{ money(listingSuggestedPrice(row.listing), row.listing.currency) }}</strong>
                    <em>{{ listingPriceSourceLabel(row.listing) }}</em>
                    <FomoButton variant="quiet" type="button" @click="setListingEditSuggestedPrice(row.listing)">Volver al sugerido</FomoButton>
                  </div>
                  <label>
                    <span>Tipo de entrada</span>
                    <select :value="listingEditField(row.listing, 'delivery_method')" @change="setListingEditField(row.listing, 'delivery_method', $event.target.value)">
                      <option v-for="method in deliveryMethods" :key="method.value" :value="method.value">{{ method.label }}</option>
                    </select>
                  </label>
                  <label v-if="isQrDeliveryMethod(listingEditField(row.listing, 'delivery_method'))">
                    <span>Forma de carga</span>
                    <select :value="listingEditField(row.listing, 'fulfillment_mode')" @change="setListingEditField(row.listing, 'fulfillment_mode', $event.target.value)">
                      <option value="per_unit">Una por entrada</option>
                      <option value="package">Un archivo para todo el paquete</option>
                    </select>
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Asientos</span>
                    <textarea rows="2" :value="listingEditField(row.listing, 'seat_labels_text')" @input="setListingEditField(row.listing, 'seat_labels_text', $event.target.value)"></textarea>
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Referencias o códigos</span>
                    <textarea rows="2" :value="listingEditField(row.listing, 'external_refs_text')" placeholder="Una por entrada o una sola si vendes paquete" @input="setListingEditField(row.listing, 'external_refs_text', $event.target.value)"></textarea>
                  </label>
                  <FomoButton variant="action" type="button" :disabled="savingListingEditKey === `listing-${row.listing.id}`" @click="saveListingEdit(row.listing)">{{ savingListingEditKey === `listing-${row.listing.id}` ? 'Guardando...' : 'Guardar cambios' }}</FomoButton>
                </div>
                <div v-else-if="row.listing.safe_actions && row.listing.safe_actions.locked_reason" class="seller-swap-delivery-lock">{{ listingLockedLabel(row.listing) }}</div>

                <div v-if="row.listing.safe_actions && row.listing.safe_actions.can_upload_proof" class="seller-swap-proof-form">
                  <label v-if="row.listing.fulfillment_mode !== 'package'">
                    <span>Entrada a actualizar</span>
                    <select :value="listingProofField(row.listing, 'unit_number') || 1" @change="setListingProofField(row.listing, 'unit_number', Number($event.target.value))"><option v-for="unit in row.listing.units" :key="unit.id" :value="unit.unit_number">Entrada {{ unit.unit_number }}{{ unit.seat_label ? ` · ${unit.seat_label}` : '' }}{{ unit.has_ticket_file ? ' · archivo guardado' : '' }}</option></select>
                  </label>
                  <label>
                    <span>Archivo o captura</span>
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" @change="setListingProofFile(row.listing, $event)" />
                  </label>
                  <label>
                    <span>Referencia externa</span>
                    <input type="text" :value="listingProofField(row.listing, 'external_reference')" placeholder="Folio, referencia o compra original" @input="setListingProofField(row.listing, 'external_reference', $event.target.value)" />
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Nota</span>
                    <input type="text" :value="listingProofField(row.listing, 'note')" placeholder="Contexto para recordar esta entrada" @input="setListingProofField(row.listing, 'note', $event.target.value)" />
                  </label>
                  <FomoButton variant="action" type="button" :disabled="uploadingProofKey === `proof-${row.listing.id}` || !canUploadProof(row.listing)" @click="uploadListingProof(row.listing)">{{ uploadingProofKey === `proof-${row.listing.id}` ? 'Subiendo...' : 'Guardar evidencia' }}</FomoButton>
                </div>

                <div class="seller-swap-actions-row">
                  <FomoButton variant="quiet" v-if="row.listing.safe_actions && row.listing.safe_actions.can_cancel" type="button" :disabled="listingActionKey === `cancel-${row.listing.id}`" @click="runListingAction(row.listing, 'cancel')">Cancelar entrada</FomoButton>
                </div>
              </template>

              <template v-else>
                <div v-if="Array.isArray(row.delivery.artifacts) && row.delivery.artifacts.length" class="seller-swap-artifact-list">
                  <article v-for="artifact in row.delivery.artifacts" :key="artifact.id">
                    <small>{{ artifact.visible_to_buyer ? 'Visible para comprador' : 'Respaldo interno' }} · {{ artifactTypeLabel(artifact.artifact_type) }}</small>
                    <strong>{{ artifact.title || 'Archivo de entrega' }}</strong>
                    <span v-if="artifact.external_reference">{{ artifact.external_reference }}</span>
                    <span v-if="artifact.instructions">{{ artifact.instructions }}</span>
                    <a v-if="artifact.file_url" :href="artifact.file_url" target="_blank" rel="noopener">{{ artifact.original_name || 'Archivo' }}</a>
                  </article>
                </div>

                <div v-if="canSubmitDeliveryHandoff(row.delivery)" class="seller-swap-delivery-form">
                  <label>
                    <span>¿Qué estás entregando?</span>
                    <select :value="handoffField(row.delivery, 'handoff_method')" @change="setHandoffField(row.delivery, 'handoff_method', $event.target.value)">
                      <option v-for="method in handoffMethods" :key="method.value" :value="method.value">{{ method.label }}</option>
                    </select>
                  </label>
                  <label>
                    <span>Referencia</span>
                    <input type="text" :value="handoffField(row.delivery, 'evidence_reference')" placeholder="Folio, enlace o referencia de la entrada" @input="setHandoffField(row.delivery, 'evidence_reference', $event.target.value)" />
                  </label>
                  <label>
                    <span>Archivo, foto o captura</span>
                    <input type="file" @change="setHandoffFile(row.delivery, $event)" />
                  </label>
                  <label>
                    <span>Detalles de entrega</span>
                    <input type="text" :value="handoffField(row.delivery, 'note')" placeholder="Cuenta como fue entregada o que debe saber el comprador" @input="setHandoffField(row.delivery, 'note', $event.target.value)" />
                  </label>
                  <FomoButton variant="primary" type="button" :disabled="savingDeliveryKey === `delivery-${row.delivery.id}` || !canSubmitDeliveryHandoffForm(row.delivery)" @click="submitDeliveryHandoff(row.delivery)">{{ savingDeliveryKey === `delivery-${row.delivery.id}` ? 'Registrando...' : 'Registrar entrega' }}</FomoButton>
                </div>
                <div v-else class="seller-swap-delivery-lock">
                  {{ deliveryLockLabel(row.delivery) }}
                </div>

                <div v-if="messageThreadForDelivery(row.delivery)" class="seller-swap-message-compose seller-swap-message-compose--inline">
                  <SwapContactCard :contact="messageThreadForDelivery(row.delivery).counterparty" />
                  <div v-for="message in messageThreadForDelivery(row.delivery).messages || []" :key="message.id">
                    <small>{{ message.sender_role === 'seller' ? 'Tú' : message.sender_role === 'buyer' ? 'Comprador' : 'FOMO' }}</small>
                    <p>{{ message.body }}</p>
                  </div>
                  <FomoButton variant="quiet" :disabled="loading" @click="loadAll({ preserveFeedback: true })">Actualizar mensajes</FomoButton>
                  <label>
                    <span>Mensaje al comprador</span>
                    <textarea :value="messageDraft(messageThreadForDelivery(row.delivery).id)" rows="2" placeholder="Escribe sobre esta venta" @input="setMessageDraft(messageThreadForDelivery(row.delivery).id, $event.target.value)"></textarea>
                  </label>
                  <FomoButton variant="quiet" type="button" :disabled="sendingMessageKey === `thread-${messageThreadForDelivery(row.delivery).id}` || !messageDraft(messageThreadForDelivery(row.delivery).id)" @click="sendSellerMessage(messageThreadForDelivery(row.delivery))">{{ sendingMessageKey === `thread-${messageThreadForDelivery(row.delivery).id}` ? 'Enviando...' : 'Enviar mensaje' }}</FomoButton>
                </div>
              </template>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSection === 'finance'" class="seller-swap-panel fomo-dashboard-card">
        <div class="seller-swap-panel__header">
          <div>
            <span class="workspace-v2__kicker">Pago</span>
            <h2>Resumen de cobros</h2>
            <p>Revisa tu cuenta de cobro y el estado de tus ventas.</p>
          </div>
          <FomoButton variant="quiet" type="button" :disabled="loading" @click="loadAll">Actualizar</FomoButton>
        </div>

        <SwapPayoutSetup :request="swapApi" :country="profile?.country_code || 'EC'" />
        <SwapBalanceSummary v-if="finance" :summary="financeSummary" :currency="financeCurrency" />
        <div class="seller-swap-finance-grid">
          <article v-for="card in financeCards" :key="card.key" class="seller-swap-finance-card fomo-dashboard-card">
            <small>{{ card.label }}</small>
            <strong>{{ money(card.value, financeCurrency) }}</strong>
            <span>{{ card.helper }}</span>
          </article>
        </div>

        <div class="seller-swap-finance-split">
          <article class="seller-swap-finance-list fomo-dashboard-card">
            <div class="seller-swap-panel__header seller-swap-panel__header--compact">
              <div>
                <span class="workspace-v2__kicker">Últimos movimientos</span>
                <h3>Ventas</h3>
              </div>
            </div>
            <div v-if="!financeRecentAllocations.length" class="seller-swap-empty">Aún no tienes ventas verificadas en Swap.</div>
            <div v-else class="seller-swap-row-list">
              <article v-for="allocation in financeRecentAllocations" :key="allocation.id" class="seller-swap-row-card fomo-dashboard-card">
                <div>
                  <strong>{{ allocation.order_number }}</strong>
                  <span>{{ allocation.event_name }} · {{ allocation.listing_title }}</span>
                  <small>{{ allocation.seat_label || 'Entrada general' }}</small><small v-if="allocation.release_at && !allocation.paid_at">Disponible desde {{ formatDate(allocation.release_at) }}, si no hay reclamos.</small><small v-if="allocation.paid_at">Enviado {{ formatDate(allocation.paid_at) }}</small>
                </div>
                <div>
                  <span :class="['seller-swap-pill', financeStatusClass(allocation.status)]">{{ financeStatusLabel(allocation.status) }}</span>
                  <strong>{{ money(allocation.seller_payable_amount, allocation.currency) }}</strong>
                </div>
              </article>
            </div>
          </article>

          <article class="seller-swap-finance-list fomo-dashboard-card">
            <div class="seller-swap-panel__header seller-swap-panel__header--compact">
              <div>
                <span class="workspace-v2__kicker">Ajustes</span>
                <h3>Reembolsos o reclamos</h3>
              </div>
            </div>
            <div v-if="!financeRecoveries.length" class="seller-swap-empty">No tienes ajustes recientes.</div>
            <div v-else class="seller-swap-row-list">
              <article v-for="recovery in financeRecoveries" :key="recovery.id" class="seller-swap-row-card fomo-dashboard-card">
                <div>
                  <strong>{{ recoveryReasonLabel(recovery.reason) }}</strong>
                  <span>{{ formatDate(recovery.created_at) }}</span>
                </div>
                <div>
                  <span :class="['seller-swap-pill', recovery.status === 'open' ? 'is-danger' : 'is-active']">{{ recoveryStatusLabel(recovery.status) }}</span>
                  <strong>{{ money(recovery.amount, recovery.currency) }}</strong>
                </div>
              </article>
            </div>
          </article>
        </div>
      </section>

      <div v-if="activeSection === 'profile'" class="seller-swap-profile-layout">
      <section class="seller-swap-panel seller-swap-profile-panel fomo-dashboard-card">
        <div class="seller-swap-panel__header">
          <div>
            <span class="workspace-v2__kicker">Datos y contacto</span>
            <h2>Tus datos de vendedor</h2>
            <p>Elige cómo te reconocerá y contactará tu comprador.</p>
          </div>
          <span :class="['seller-swap-pill', profileStatusClass]">{{ profileStatusLabel }}</span>
        </div>

        <div v-if="profileIsLoading" class="seller-swap-empty">Cargando perfil Swap...</div>
        <form v-else class="seller-swap-profile-form" @submit.prevent="saveProfile">
          <fieldset class="seller-swap-profile-fields" :disabled="savingProfile">
            <legend>Tu identidad</legend>
            <div class="seller-swap-form seller-swap-profile-fields__grid">
            <label>
              <span>Nombre público</span>
              <input v-model.trim="profileForm.public_name" type="text" autocomplete="nickname" maxlength="160" placeholder="Ej. Ana Torres" required aria-describedby="swap-public-name-help" />
              <small id="swap-public-name-help">Usa tu nombre o apodo. Así te verá tu comprador.</small>
            </label>
            <label>
              <span>Nombre legal</span>
              <input v-model.trim="profileForm.legal_name" type="text" autocomplete="name" maxlength="240" placeholder="Nombre completo o razón social" />
              <small>Para tus datos de cuenta; no se muestra en tu contacto público.</small>
            </label>
            </div>
          </fieldset>
          <fieldset class="seller-swap-profile-fields" :disabled="savingProfile">
            <legend>Contacto para la entrega</legend>
            <div class="seller-swap-form seller-swap-profile-fields__grid">
            <div class="seller-swap-profile-email"><span>Correo de tu cuenta</span><strong>{{ profile?.user_email || 'El correo con el que ingresaste a FOMO' }}</strong><small>Se comparte con tu comprador después de confirmar el pago.</small></div>
            <FomoPhoneInput id="swap-profile-phone" v-model="profileForm.contact_phone" label="WhatsApp (opcional)" :required="profileForm.whatsapp_enabled" />
            <label class="seller-swap-check seller-swap-profile-optin">
              <input v-model="profileForm.whatsapp_enabled" type="checkbox" />
              <span>Compartir mi WhatsApp con quienes compren mis entradas, después del pago.</span>
            </label>
            </div>
          </fieldset>
          <div class="seller-swap-profile-terms">
            <p v-if="profile?.terms_accepted_at"><svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor"/><path d="m6 10 3 3 5-6" stroke="currentColor" stroke-width="1.5"/></svg> Términos aceptados</p>
            <label v-else class="seller-swap-check"><input v-model="profileForm.terms_accepted" type="checkbox" required /><span>Acepto los términos de FOMO Swap</span></label>
          </div>
          <footer class="seller-swap-profile-footer">
            <span role="status">{{ profileDirty ? 'Tienes cambios sin guardar' : 'Tus datos están guardados' }}</span>
            <FomoButton variant="text" :disabled="savingProfile || !profileDirty" @click="syncProfileForm">Descartar cambios</FomoButton>
            <FomoButton variant="action" type="submit" :disabled="savingProfile || !profileDirty">{{ savingProfile ? 'Guardando…' : 'Guardar perfil' }}</FomoButton>
          </footer>
        </form>
      </section>
      <aside class="seller-swap-profile-preview fomo-dashboard-card" aria-label="Vista previa de tu contacto">
        <span class="workspace-v2__kicker">Vista previa</span>
        <h2>Así te ve tu comprador</h2>
        <div class="seller-swap-profile-person">
          <span class="seller-swap-profile-avatar" aria-hidden="true">{{ profileInitials }}</span>
          <div><strong>{{ profileForm.public_name || 'Tu nombre público' }}</strong><span>Vendedor en FOMO Swap</span></div>
        </div>
        <div class="seller-swap-profile-contact-preview">
          <span>Después de confirmar el pago</span>
          <strong>{{ profile?.user_email || 'Tu correo de contacto' }}</strong>
          <strong v-if="profileForm.whatsapp_enabled && profileForm.contact_phone">{{ profileForm.contact_phone }}</strong>
          <small v-else>WhatsApp no compartido</small>
        </div>
        <div class="seller-swap-profile-protection">
          <svg viewBox="0 0 48 48" width="48" height="48" fill="none" aria-hidden="true"><path d="m24 5 15 6v11c0 11-15 21-15 21S9 33 9 22V11l15-6Z" stroke="currentColor" stroke-width="1.5"/><path d="m16 24 6 6 11-13" stroke="currentColor" stroke-width="2"/></svg>
          <div><h3>El contacto llega con la compra</h3><p>Estos datos se comparten con quien te compra. El historial de mensajes queda en FOMO.</p></div>
        </div>
      </aside>
      </div>
      </main>
    </div>
  </div>
</template>

<script>
import { apiService, apiServiceAuth, apiServiceAuthForm, apiServiceForm } from '../../common/api.services.js'
import FomoButton from '../../components/ui/FomoButton.vue'
import FomoPhoneInput from '../../components/ui/FomoPhoneInput.vue'
import SwapShareLink from '../../components/swap/SwapShareLink.vue'
import SwapListingWizard from '../../components/swap/SwapListingWizard.vue'
import SwapPublicationSuccess from '../../components/swap/SwapPublicationSuccess.vue'
import SwapPayoutSetup from '../../components/swap/SwapPayoutSetup.vue'
import SwapContactCard from '../../components/swap/SwapContactCard.vue'
import SwapBalanceSummary from '../../components/swap/SwapBalanceSummary.vue'
import { swapDeliveryProgress } from '../../common/swapDeliveryStatus'
import '@/styles/fomo-dashboard.css'
import OrganizerWorkspaceNav from '../../components/workspace/OrganizerWorkspaceNav.vue'
import OrganizerWorkspaceTopbar from '../../components/workspace/OrganizerWorkspaceTopbar.vue'

export default {
  name: 'SellerSwapWorkspaceView',

  components: {
    FomoButton, FomoPhoneInput, SwapShareLink, SwapListingWizard, SwapPublicationSuccess, SwapPayoutSetup, SwapContactCard, SwapBalanceSummary,
    OrganizerWorkspaceNav,
    OrganizerWorkspaceTopbar,
  },

  props: {
    section: {
      type: String,
      default: 'sell',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    showEmbeddedMenu: {
      type: Boolean,
      default: true,
    },
    authKind: {
      type: String,
      default: 'organizer',
    },
  },
  data() {
    return {
      wizardKey: 0,
      publishedListing: null,
      sellerOwnerId: '',
      showNav: false,
      isMobileViewport: typeof window !== 'undefined' ? window.matchMedia('(max-width: 980px)').matches : false,
      loading: false,
      savingProfile: false,
      savingListing: false,
      savingDeliveryKey: '',
      savingListingEditKey: '',
      listingActionKey: '',
      uploadingProofKey: '',
      sendingMessageKey: '',
      embeddedSection: this.section || 'sell',
      entryFilter: 'all',
      expandedEntryKey: '',
      saleStep: 'event',
      pricingOptionsPayload: null,
      pricingOptionsLoading: false,
      feedback: '',
      feedbackType: 'success',
      profile: null,
      market: null,
      events: [],
      listings: [],
      deliveries: [],
      messageThreads: [],
      finance: null,
      deliveryHandoffForms: {},
      messageDrafts: {},
      listingEditForms: {},
      listingProofForms: {},
      profileForm: this.defaultProfileForm(),
      listingForm: this.defaultListingForm(),
    }
  },
  computed: {
    navItems() {
      return [
        {
          key: 'sell',
          label: 'Vender',
          helper: 'Nueva entrada',
          icon: 'fas fa-plus-circle',
          routeName: 'swap-seller-sell',
          title: 'Vende tus entradas',
          description: 'Publica, entrega y sigue tus cobros desde aquí.',
        },
        {
          key: 'tickets',
          label: 'Mis entradas',
          helper: 'Ventas y entrega',
          icon: 'fas fa-ticket',
          routeName: 'swap-seller-tickets',
          title: 'Entradas y ventas',
          description: 'Gestiona publicación, entrega y mensajes desde una sola lista.',
        },
        {
          key: 'finance',
          label: 'Cobros',
          helper: 'Ventas y ajustes',
          icon: 'fas fa-wallet',
          routeName: 'swap-seller-finance',
          title: 'Tu dinero',
          description: 'Consulta ventas verificadas, montos por cobrar, pagos realizados y ajustes.',
        },
        {
          key: 'profile',
          label: 'Perfil y ayuda',
          helper: 'Habilitación',
          icon: 'fas fa-user-shield',
          routeName: 'swap-seller-profile',
          title: 'Perfil de vendedor',
          description: 'Mantén tus datos y términos listos para vender.',
        },
      ]
    },
    swapSellerHomeRoute() {
      return { name: 'swap-seller-sell' }
    },
    sellerWorkspaceNavItems() {
      const routeName = String((this.$route && this.$route.name) || '')
      return [
        {
          label: 'FOMO Swap',
          icon: 'fas fa-right-left',
          to: this.swapSellerHomeRoute,
          active: routeName === 'swap-seller' || routeName.startsWith('swap-seller-'),
        },
      ]
    },
    sellerContextNavItems() {
      return this.navItems.map((item) => ({
        label: item.label,
        icon: item.icon,
        to: { name: item.routeName },
        active: item.key === this.activeSection,
      }))
    },
    sellerNavContextLabel() {
      if (this.profileStatusLabel && this.profileStatusLabel !== 'Sin perfil') {
        return this.profileStatusLabel
      }
      return 'FOMO Swap'
    },
    activeSection() {
      if (this.embedded) {
        const embeddedValue = this.embeddedSection || 'sell'
        if (['deliveries', 'messages'].includes(embeddedValue)) return 'tickets'
        return this.navItems.some((item) => item.key === embeddedValue) ? embeddedValue : 'sell'
      }
      const value = this.section || (this.$route && this.$route.params && this.$route.params.section) || 'sell'
      if (['deliveries', 'messages'].includes(value)) return 'tickets'
      return this.navItems.some((item) => item.key === value) ? value : 'sell'
    },
    activeSectionConfig() {
      return this.navItems.find((item) => item.key === this.activeSection) || this.navItems[0]
    },
    profileIsLoading() {
      return this.loading && !this.profile
    },
    profileNeedsCompletion() {
      return !this.profileIsLoading && (!this.profile || !this.profile.terms_accepted_at)
    },
    profileDirty() {
      const saved = this.profile || {}
      return ['public_name', 'legal_name', 'contact_phone'].some(key => (this.profileForm[key] || '') !== (saved[key] || ''))
        || Boolean(this.profileForm.whatsapp_enabled) !== Boolean(saved.whatsapp_enabled)
        || Boolean(this.profileForm.terms_accepted) !== Boolean(saved.terms_accepted_at)
    },
    profileInitials() { return (this.profileForm.public_name || '').trim().split(/\s+/).slice(0, 2).map(word => word[0] || '').join('').toUpperCase() || 'TU' },
    profileStatusLabel() {
      if (this.loading && !this.profile) return 'Cargando'
      if (!this.profile) return 'Sin perfil'
      const labels = {
        active: 'Activo',
        pending: 'Pendiente',
        review: 'Con revisión',
        pending_review: 'Con revisión',
        blocked: 'Bloqueado',
        rejected: 'Requiere cambios',
        inactive: 'Inactivo',
      }
      return labels[this.profile.status] || this.humanizeStatus(this.profile.status)
    },
    profileStatusClass() {
      if (this.loading && !this.profile) return 'is-warning'
      if (!this.profile) return 'is-muted'
      if (this.profile.status === 'active') return 'is-active'
      if (this.profile.status === 'blocked') return 'is-danger'
      return 'is-warning'
    },
    heroActionLabel() {
      if (this.profileIsLoading) return 'Cargando'
      if (this.profileNeedsCompletion) return 'Completar perfil'
      if (this.activeSection === 'sell') return 'Ver entradas'
      return 'Actualizar datos'
    },
    heroActionIcon() {
      if (this.profileIsLoading) return 'fas fa-rotate'
      if (this.profileNeedsCompletion) return 'fas fa-user-shield'
      if (this.activeSection === 'sell') return 'fas fa-ticket'
      return 'fas fa-rotate'
    },
    heroActionDisabled() {
      return this.loading
    },
    canSubmitListing() {
      return Boolean(
        this.profile
        && this.profile.terms_accepted_at
        && this.listingForm.event
        && this.listingForm.official_ticket
        && this.listingForm.quantity
        && this.listingForm.unit_price
        && !this.priceBelowOfficialBlocked
        && this.deliveryEvidenceReady
      )
    },
    sellSteps() {
      return [
        { key: 'event', label: 'Evento' },
        { key: 'category', label: 'Tipo' },
        { key: 'quantity', label: 'Cantidad' },
        { key: 'price', label: 'Precio' },
        { key: 'delivery', label: 'Entrega' },
        { key: 'publish', label: 'Publicar' },
      ]
    },
    sellProgress() {
      const index = this.sellSteps.findIndex((step) => step.key === this.saleStep)
      return Math.max(12, Math.round(((index + 1) / this.sellSteps.length) * 100))
    },
    selectedEvent() {
      return this.events.find((event) => Number(event.id) === Number(this.listingForm.event)) || null
    },
    pricingOptions() {
      return this.pricingOptionsPayload && Array.isArray(this.pricingOptionsPayload.options) ? this.pricingOptionsPayload.options : []
    },
    selectedPricingOption() {
      return this.pricingOptions.find((option) => Number(option.id) === Number(this.listingForm.official_ticket)) || null
    },
    suggestedPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.recommended_price || 0) : 0
    },
    officialPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.official_price || 0) : 0
    },
    marketReferencePrice() {
      if (!this.selectedPricingOption) return 0
      const market = Number(this.selectedPricingOption.market_price || 0)
      return market > 0 ? market : Number(this.selectedPricingOption.recommended_price || 0)
    },
    minimumAllowedPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.minimum_allowed_price || 0) : 0
    },
    priceBelowOfficialBlocked() {
      if (!this.selectedPricingOption || this.selectedPricingOption.allow_below_official_price) return false
      return Number(this.listingForm.unit_price || 0) > 0 && Number(this.listingForm.unit_price || 0) < this.minimumAllowedPrice
    },
    pricePolicyWarning() {
      if (!this.selectedPricingOption) return ''
      if (this.priceBelowOfficialBlocked) return `El precio mínimo para esta categoría es el precio oficial: ${this.money(this.minimumAllowedPrice, 'USD')}.`
      if (this.selectedPricingOption.allow_below_official_price && Number(this.listingForm.unit_price || 0) < this.officialPrice) return 'FOMO permite publicar bajo precio oficial para este evento, pero el sugerido se mantiene como referencia.'
      return ''
    },
    quantityChoices() {
      if (this.selectedPricingOption && this.selectedPricingOption.is_package) return [1]
      return [1, 2, 3, 4, 5]
    },
    estimatedSellerTotal() {
      return Number(this.listingForm.quantity || 0) * Number(this.listingForm.unit_price || this.suggestedPrice || 0)
    },
    externalRefLines() {
      return this.parseLines(this.listingForm.external_refs_text)
    },
    externalRefsReadyForUnits() {
      const count = this.externalRefLines.length
      if (!count) return false
      if (this.listingForm.fulfillment_mode === 'package') return count === 1
      return count === Number(this.listingForm.quantity || 0)
    },
    deliveryEvidenceRequired() {
      return this.isQrDeliveryMethod(this.listingForm.delivery_method)
    },
    deliveryEvidenceReady() {
      if (!this.deliveryEvidenceRequired) return true
      return Boolean(this.listingForm.proof_file || this.externalRefsReadyForUnits)
    },
    livePreviewProofClass() {
      if (this.deliveryEvidenceReady) return 'is-ready'
      if (!this.selectedPricingOption || !['delivery', 'publish'].includes(this.saleStep)) return 'is-neutral'
      return ''
    },
    livePreviewProofIcon() {
      if (this.deliveryEvidenceReady) return 'fas fa-circle-check'
      if (!this.selectedPricingOption || !['delivery', 'publish'].includes(this.saleStep)) return 'fas fa-info-circle'
      return 'fas fa-triangle-exclamation'
    },
    livePreviewProofLabel() {
      if (!this.selectedPricingOption) return 'Elige tipo de entrada'
      if (!this.deliveryEvidenceRequired) return 'Entrega coordinada'
      if (this.deliveryEvidenceReady) return 'QR/evidencia lista'
      if (!['delivery', 'publish'].includes(this.saleStep)) return 'QR se pedirá antes de publicar'
      return 'Falta QR o referencia'
    },
    deliveryPrimaryProofLabel() {
      if (this.listingForm.delivery_method === 'qr_redeem_physical') return 'Sube el QR canjeable o temporal'
      return 'Sube el QR final de la entrada'
    },
    deliveryProofHelper() {
      if (this.listingForm.delivery_method === 'qr_redeem_physical') {
        return 'Este QR permite retirar o canjear una entrada física. Déjalo claro para que FOMO y el comprador sepan que no es el acceso final directo.'
      }
      return 'Este debe ser el QR o PDF final de acceso. FOMO lo resguarda y lo libera al comprador solo después de verificar el pago.'
    },
    listingProofDraftFileName() {
      return this.listingForm.proof_file ? this.listingForm.proof_file.name : 'Subir QR, PDF o captura'
    },
    sellerReferencePlaceholder() {
      if (this.listingForm.fulfillment_mode === 'package') return 'Una referencia del paquete completo si no subes archivo'
      return 'Una referencia por entrada si no subes archivo\nEj: QR-001\nQR-002'
    },
    referenceCountHint() {
      if (this.listingForm.proof_file) return 'Si subes archivo, la referencia ayuda a FOMO pero no es obligatoria.'
      if (this.listingForm.fulfillment_mode === 'package') return this.externalRefsReadyForUnits ? 'Referencia de paquete lista.' : 'Necesitas 1 referencia si no subes archivo.'
      const quantity = Number(this.listingForm.quantity || 0)
      const count = this.externalRefLines.length
      return this.externalRefsReadyForUnits ? `${count} referencia${count === 1 ? '' : 's'} lista${count === 1 ? '' : 's'}.` : `Necesitas ${quantity} referencia${quantity === 1 ? '' : 's'} si no subes archivo.`
    },
    publishPreviewTitle() {
      return (this.selectedPricingOption && this.selectedPricingOption.name) || this.listingForm.title || 'Tu entrada FOMO Swap'
    },
    financeCurrency() {
      return (this.finance && this.finance.currency) || 'USD'
    },
    financeSummary() {
      return (this.finance && this.finance.summary) || {}
    },
    financeCards() {
      const summary = this.financeSummary
      return [
        { key: 'estimated', label: 'Ventas confirmadas', value: summary.estimated, helper: 'Compras con pago verificado' },
        { key: 'open_recovery', label: 'Ajustes', value: summary.open_recovery, helper: 'Reembolsos o reclamos' },
      ]
    },
    financeRecentAllocations() {
      return this.finance && Array.isArray(this.finance.recent_allocations) ? this.finance.recent_allocations : []
    },
    financeRecoveries() {
      return this.finance && Array.isArray(this.finance.recoveries) ? this.finance.recoveries : []
    },
    deliveryMethods() {
      return [
        { value: 'physical_handoff', label: 'Entrega física' },
        { value: 'physical_shipping', label: 'Envío físico' },
        { value: 'app_transfer', label: 'Transferido por app' },
        { value: 'qr_final_ticket', label: 'Envío QR (entrada final)' },
        { value: 'qr_redeem_physical', label: 'Envío QR (debe canjear luego físico)' },
      ]
    },
    handoffMethods() {
      return [
        { value: 'physical_handoff', label: 'Entrega física' },
        { value: 'physical_shipping', label: 'Envío físico' },
        { value: 'app_transfer', label: 'Transferido por app' },
      ]
    },
    deliveryListingIds() {
      return new Set(this.deliveries.map((delivery) => delivery && delivery.item && delivery.item.listing).filter(Boolean))
    },
    entryRows() {
      const deliveryRows = this.deliveries.map((delivery) => this.deliveryEntryRow(delivery)).filter(Boolean)
      const listingRows = this.listings
        .filter((listing) => !this.deliveryListingIds.has(listing.id) || this.listingAvailableQuantity(listing) > 0)
        .map((listing) => this.listingEntryRow(listing))
        .filter(Boolean)
      return [...deliveryRows, ...listingRows]
    },
    filteredEntryRows() {
      if (this.entryFilter === 'all') return this.entryRows
      return this.entryRows.filter((row) => row.filterKeys.includes(this.entryFilter))
    },
    entryFilters() {
      const filters = [
        { key: 'all', label: 'Todas' },
        { key: 'published', label: 'Publicadas' },
        { key: 'sold', label: 'Vendidas' },
        { key: 'to_deliver', label: 'Por entregar' },
        { key: 'delivered', label: 'Entregadas' },
        { key: 'issue', label: 'Con problema' },
      ]
      return filters.map((filter) => ({
        ...filter,
        count: filter.key === 'all' ? this.entryRows.length : this.entryRows.filter((row) => row.filterKeys.includes(filter.key)).length,
      }))
    },
  },
  created() {
    this.loadAll()
  },
  mounted() {
    this.handleViewportChange = () => {
      this.isMobileViewport = window.matchMedia('(max-width: 980px)').matches
      if (!this.isMobileViewport) this.showNav = false
    }
    this.handleViewportChange()
    window.addEventListener('resize', this.handleViewportChange)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportChange)
  },
  watch: {
    section(value) {
      if (this.embedded && value) this.embeddedSection = value
    },
  },
  methods: {
    async onListingPublished(listing) {
      this.feedback = listing.status === 'pending_review' ? 'Publicación enviada a revisión.' : 'Tus entradas ya están publicadas.'
      this.feedbackType = 'success'
      this.wizardKey++
      this.publishedListing = listing
      this.listings = [listing, ...this.listings.filter(item => item.id !== listing.id)]
      this.syncListingForms()
    },
    async viewPublishedListings() {
      this.publishedListing = null
      if (this.embedded) this.setEmbeddedSection('tickets')
      else this.$router.push({ name: 'swap-seller-tickets' })
      await this.loadAll({ preserveFeedback: true })
    },
    setEmbeddedSection(section) {
      this.embeddedSection = section
    },
    swapApi(endpoint, method, data, options = {}) {
      if (this.authKind === 'ticket') return apiServiceAuth(endpoint, method, data, undefined, 'ticket', options)
      return apiService(endpoint, method, data, options)
    },
    swapApiForm(endpoint, method, data, options = {}) {
      if (this.authKind === 'ticket') return apiServiceAuthForm(endpoint, method, data, undefined, 'ticket', options)
      return apiServiceForm(endpoint, method, data, options)
    },
    defaultProfileForm() {
      return { public_name: '', legal_name: '', contact_phone: '', whatsapp_enabled: false, terms_accepted: false, email_notifications_enabled: true }
    },
    defaultListingForm() {
      return {
        event: '',
        official_ticket: '',
        title: '',
        section_label: '',
        row_label: '',
        quantity: 1,
        unit_price: '',
        currency: 'USD',
        delivery_method: 'qr_final_ticket',
        fulfillment_mode: 'per_unit',
        seats_together: true,
        seat_labels_text: '',
        external_refs_text: '',
        proof_file: null,
        proof_note: '',
      }
    },
    quantityLabel(quantity) {
      const value = Number(quantity || 0)
      if (this.selectedPricingOption && this.selectedPricingOption.is_package) return `${this.selectedPricingOption.name} completo`
      return `${value} ${value === 1 ? 'entrada' : 'entradas'}`
    },
    categoryKindLabel(option) {
      if (!option) return 'Categoría oficial'
      if (option.is_package) return 'Paquete completo'
      const name = String(option.name || '').toLowerCase()
      if (name.includes('vip') || name.includes('palco') || name.includes('mesa') || name.includes('fila')) return 'Entrada numerada o premium'
      return 'Entrada individual'
    },
    categoryIcon(option) {
      if (!option) return 'fas fa-ticket-alt'
      const name = String(option.name || '').toLowerCase()
      if (option.is_package) return 'fas fa-layer-group'
      if (name.includes('vip') || name.includes('palco')) return 'fas fa-crown'
      if (name.includes('mesa')) return 'fas fa-glass-cheers'
      if (name.includes('fila') || name.includes('seat') || name.includes('silla')) return 'fas fa-chair'
      return 'fas fa-ticket-alt'
    },
    categoryCardCopy(option) {
      if (!option) return 'Elige una categoría oficial para continuar.'
      const max = Number(option.max_quantity || option.quantity || 1)
      const base = option.description || `Máximo sugerido por compra: ${max} ${max === 1 ? 'entrada' : 'entradas'}.`
      return `${base} Precio oficial ${this.money(option.official_price, option.currency)}.`
    },
    sellStepIsDone(key) {
      if (key === 'event') return Boolean(this.listingForm.event)
      if (key === 'category') return Boolean(this.listingForm.official_ticket)
      if (key === 'quantity') return Boolean(this.listingForm.quantity)
      if (key === 'price') return Boolean(this.listingForm.unit_price) && !this.priceBelowOfficialBlocked
      if (key === 'delivery') return Boolean(this.listingForm.delivery_method) && this.deliveryEvidenceReady
      if (key === 'publish') return this.canSubmitListing
      return false
    },
    goSaleStep(step) {
      this.saleStep = step
    },
    async selectListingEvent(event) {
      if (!event || !event.id) return
      this.listingForm.event = event.id
      this.listingForm.official_ticket = ''
      this.listingForm.title = ''
      this.listingForm.section_label = ''
      this.listingForm.unit_price = ''
      await this.loadPricingOptions(event.id)
      this.saleStep = 'category'
    },
    async loadPricingOptions(eventId) {
      if (!eventId) {
        this.pricingOptionsPayload = null
        return
      }
      this.pricingOptionsLoading = true
      const data = await this.swapApi(`/api/swap/seller/events/${encodeURIComponent(eventId)}/pricing-options/`)
      this.pricingOptionsLoading = false
      if (data && Array.isArray(data.options) && !data.err) {
        this.pricingOptionsPayload = data
        return
      }
      this.pricingOptionsPayload = null
      this.feedback = this.extractError(data, 'No pudimos calcular precios sugeridos para este evento.')
      this.feedbackType = 'error'
    },
    selectPricingOption(option) {
      if (!option || !option.id) return
      this.listingForm.official_ticket = option.id
      this.listingForm.title = option.name
      this.listingForm.section_label = option.name
      this.listingForm.quantity = option.is_package ? 1 : Math.max(1, Number(this.listingForm.quantity || 1))
      this.listingForm.fulfillment_mode = option.is_package ? 'package' : 'per_unit'
      this.listingForm.unit_price = option.recommended_price
      this.saleStep = 'quantity'
    },
    setListingQuantity(quantity) {
      this.listingForm.quantity = Number(quantity || 1)
    },
    useSuggestedPrice() {
      if (!this.selectedPricingOption) return
      this.listingForm.unit_price = this.selectedPricingOption.recommended_price
    },
    setListingDraftProofFile(event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.listingForm.proof_file = file
    },
    listingExternalRefsForPayload() {
      const refs = this.externalRefLines
      if (!refs.length) return []
      if (this.listingForm.fulfillment_mode === 'package') return refs.length === 1 ? refs : []
      return refs.length === Number(this.listingForm.quantity || 0) ? refs : []
    },
    deliveryChoiceCopy(method) {
      const copy = {
        physical_handoff: 'La entrega se coordina solo cuando el pago esté confirmado.',
        physical_shipping: 'Envías físicamente y luego registras guía o evidencia.',
        app_transfer: 'La transfieres en una app externa y guardas la referencia.',
        qr_final_ticket: 'Es el archivo final de acceso. Súbelo ahora para quedar listo.',
        qr_redeem_physical: 'Es un QR temporal o canjeable; el comprador debe retirar físico después.',
      }
      return copy[method] || 'Entrega protegida por FOMO.'
    },
    deliveryChoiceBadge(method) {
      const badges = {
        physical_handoff: 'Entrega coordinada',
        physical_shipping: 'Requiere envío',
        app_transfer: 'Transferencia externa',
        qr_final_ticket: 'QR final',
        qr_redeem_physical: 'QR temporal/canje',
      }
      return badges[method] || 'Protegida'
    },
    deliveryMethodIcon(method) {
      const icons = {
        physical_handoff: 'fas fa-handshake',
        physical_shipping: 'fas fa-truck',
        app_transfer: 'fas fa-mobile-alt',
        qr_final_ticket: 'fas fa-qrcode',
        qr_redeem_physical: 'fas fa-ticket-alt',
      }
      return icons[method] || 'fas fa-ticket-alt'
    },
    formatDate(value) {
      if (!value) return 'Fecha pendiente'
      try { return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) } catch (_error) { return value }
    },
    money(value, currency = 'USD') {
      const amount = Number(value || 0)
      try { return new Intl.NumberFormat('es-EC', { style: 'currency', currency: currency || 'USD' }).format(amount) } catch (_error) { return `${amount.toFixed(2)} ${currency || 'USD'}` }
    },
    listingAvailableQuantity(listing) {
      const availability = listing && listing.availability ? listing.availability : null
      if (availability && Number.isFinite(Number(availability.available_units))) return Number(availability.available_units)
      return Number((listing && listing.quantity) || 0)
    },
    proofStatusClass(listing) {
      const status = listing && listing.proof_status
      if (status === 'approved') return 'is-active'
      if (status === 'rejected') return 'is-danger'
      return 'is-warning'
    },
    listingEntryRow(listing) {
      if (!listing || !listing.id) return null
      const available = this.listingAvailableQuantity(listing)
      const filterKeys = listing.status === 'approved' ? ['published'] : []
      if (['rejected', 'blocked', 'cancelled'].includes(listing.status)) filterKeys.push('issue')
      return {
        key: `listing-${listing.id}`,
        kind: 'listing',
        listing,
        title: listing.title || listing.event_name || 'Entrada',
        subtitle: [listing.event_name, [listing.section_label, listing.row_label].filter(Boolean).join(' · ')].filter(Boolean).join(' · ') || 'Zona pendiente',
        seat: this.seatSummary(listing),
        eventDate: this.formatDate(listing.event_start_date),
        primaryStatusLabel: this.listingStatusLabel(listing.status),
        primaryStatusClass: this.listingStatusClass(listing.status),
        secondaryStatusLabel: this.proofStatusLabel(listing),
        secondaryStatusClass: this.proofStatusClass(listing),
        moneyKicker: listing.status === 'draft' ? 'Borrador sin publicar' : available === 1 ? '1 disponible' : `${available} disponibles`,
        amountLabel: `${listing.quantity} x ${this.money(listing.unit_price, listing.currency)}`,
        moneyHelper: this.deliveryMethodLabel(listing.delivery_method),
        primaryActionLabel: 'Ajustar precio',
        filterKeys,
      }
    },
    deliveryEntryRow(delivery) {
      if (!delivery || !delivery.id) return null
      const item = delivery.item || {}
      const filterKeys = ['sold']
      if (['pending_seller_action', 'seller_confirmed', 'delivery_in_progress', 'claim_window_open'].includes(delivery.status)) filterKeys.push('to_deliver')
      if (['delivered', 'buyer_accessible', 'released'].includes(delivery.status)) filterKeys.push('delivered')
      if (['cancelled', 'disputed', 'replaced'].includes(delivery.status) || ['failed', 'refunded', 'chargeback'].includes(delivery.order_payment_status)) filterKeys.push('issue')
      return {
        key: `delivery-${delivery.id}`,
        kind: 'delivery',
        delivery,
        title: this.deliveryItemTitle(delivery),
        subtitle: [delivery.event_name, this.deliveryMethodLabel(delivery.delivery_method)].filter(Boolean).join(' · '),
        seat: this.deliverySeatSummary(delivery),
        orderNumber: delivery.order_number,
        primaryStatusLabel: 'Vendida',
        primaryStatusClass: 'is-active',
        secondaryStatusLabel: swapDeliveryProgress(delivery, delivery.order_payment_status).label,
        secondaryStatusClass: this.deliveryStatusClass(delivery.status),
        moneyKicker: `Pago ${this.paymentStatusLabel(delivery.order_payment_status)}`,
        amountLabel: this.money(item.unit_price, item.currency || 'USD'),
        moneyHelper: delivery.order_number,
        primaryActionLabel: this.canSubmitDeliveryHandoff(delivery) ? 'Anunciar entrega' : 'Entrega y mensaje',
        filterKeys,
      }
    },
    toggleEntryDetails(key) {
      this.expandedEntryKey = this.expandedEntryKey === key ? '' : key
    },
    messageThreadForDelivery(delivery) {
      if (!delivery) return null
      const orderItemId = delivery.item && delivery.item.order_item ? Number(delivery.item.order_item) : null
      return this.messageThreads.find((thread) => {
        const threadOrderItemId = thread && thread.order_item ? Number(thread.order_item) : null
        if (orderItemId && threadOrderItemId && orderItemId === threadOrderItemId) return true
        return thread && delivery.order_number && thread.order_number === delivery.order_number
      }) || null
    },
    handleHeroAction() {
      if (this.profileNeedsCompletion) {
        if (this.embedded) {
          this.setEmbeddedSection('profile')
          return
        }
        this.$router.push({ name: 'swap-seller-profile' })
        return
      }
      if (this.activeSection === 'sell') {
        if (this.embedded) {
          this.setEmbeddedSection('tickets')
          return
        }
        this.$router.push({ name: 'swap-seller-tickets' })
        return
      }
      this.loadAll()
    },
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (payload.err) return payload.err
      if (payload.detail) return Array.isArray(payload.detail) ? payload.detail.join(', ') : payload.detail
      const firstValue = typeof payload === 'object' ? Object.values(payload)[0] : null
      if (Array.isArray(firstValue)) return firstValue[0]
      return firstValue || fallback
    },
    parseLines(value) {
      return String(value || '').split(/[\n,]+/).map((item) => item.trim()).filter(Boolean)
    },
    humanizeStatus(value) {
      if (!value) return 'Pendiente'
      return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
    listingStatusLabel(status) {
      const labels = {
        draft: 'Borrador',
        pending_review: 'Con revisión',
        approved: 'Publicada',
        paused: 'Pausada',
        rejected: 'Requiere cambios',
        blocked: 'Bloqueada',
        cancelled: 'Cancelada',
        sold_out: 'Vendida',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    deliveryStatusLabel(status) {
      const labels = {
        pending_seller_action: 'Por entregar',
        seller_confirmed: 'Entrega registrada',
        delivery_in_progress: 'En proceso',
        delivered: 'Entregada',
        buyer_accessible: 'Lista para comprador',
        claim_window_open: 'En periodo de seguridad',
        released: 'Completada',
        cancelled: 'Cancelada',
        disputed: 'Con reclamo',
        replaced: 'Reemplazada',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    paymentStatusLabel(status) {
      const labels = {
        pending: 'pendiente',
        manual_review: 'en revisión',
        verified: 'confirmado',
        failed: 'fallido',
        refunded: 'reembolsado',
        chargeback: 'con reclamo',
      }
      return labels[status] || String(this.humanizeStatus(status)).toLowerCase()
    },
    financeStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        delivery_pending: 'Por entregar',
        delivered: 'Entregado',
        held: 'Por cobrar',
        releasable: 'Disponible',
        reserved_for_payout: 'Pago en proceso',
        paid: 'Pagado',
        disputed: 'Con reclamo',
        refunded: 'Reembolsado',
        cancelled: 'Cancelado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    recoveryStatusLabel(status) {
      const labels = {
        open: 'Abierto',
        closed: 'Cerrado',
        waived: 'Cerrado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    recoveryReasonLabel(reason) {
      const normalized = String(reason || '').toLowerCase()
      if (normalized.includes('chargeback')) return 'Ajuste por reclamo'
      if (normalized.includes('refund')) return 'Ajuste por reembolso'
      return reason ? this.humanizeStatus(reason) : 'Ajuste de venta'
    },
    deliveryMethodLabel(method) {
      const labels = {
        physical_handoff: 'Entrega física',
        physical_shipping: 'Envío físico',
        app_transfer: 'Transferido por app',
        qr_final_ticket: 'Envío QR (entrada final)',
        qr_redeem_physical: 'Envío QR (debe canjear luego físico)',
        coordinated: 'Entrega física',
        upload: 'Envío QR (entrada final)',
        external_transfer: 'Transferido por app',
      }
      return labels[method] || this.humanizeStatus(method)
    },
    artifactTypeLabel(type) {
      const labels = {
        ticket_file: 'Archivo de entrada',
        transfer_reference: 'Referencia',
        delivery_note: 'Nota de entrega',
        refund_proof: 'Comprobante',
      }
      return labels[type] || this.humanizeStatus(type)
    },
    messageSenderLabel(role) {
      const labels = {
        system: 'FOMO',
        admin: 'FOMO',
        seller: 'Tú',
        buyer: 'Comprador',
      }
      return labels[role] || this.humanizeStatus(role)
    },
    messageThreadStatusLabel(status) {
      const labels = {
        open: 'Abierto',
        active: 'Activo',
        closed: 'Cerrado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    listingStatusClass(status) {
      if (status === 'approved') return 'is-active'
      if (['rejected', 'blocked', 'cancelled'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    deliveryStatusClass(status) {
      if (['seller_confirmed', 'delivery_in_progress', 'delivered', 'buyer_accessible', 'claim_window_open', 'released'].includes(status)) return 'is-active'
      if (['cancelled', 'disputed', 'replaced'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    paymentStatusClass(status) {
      if (status === 'verified') return 'is-active'
      if (['failed', 'refunded', 'chargeback'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    financeStatusClass(status) {
      if (['delivered', 'releasable', 'paid'].includes(status)) return 'is-active'
      if (['disputed', 'refunded', 'cancelled'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    seatSummary(listing) {
      const seats = Array.isArray(listing.units) ? listing.units.map((unit) => unit.seat_label).filter(Boolean) : []
      if (seats.length) return seats.join(', ')
      return listing.seats_together ? 'Asientos juntos' : 'Asientos no juntos'
    },
    unitSeatText(listing) {
      return Array.isArray(listing && listing.units) ? listing.units.map((unit) => unit.seat_label).filter(Boolean).join(', ') : ''
    },
    unitExternalRefText(listing) {
      return Array.isArray(listing && listing.units) ? listing.units.map((unit) => unit.external_ticket_ref).filter(Boolean).join('\n') : ''
    },
    listingPricingContext(listing) {
      return (listing && listing.pricing_context) || {}
    },
    listingSuggestedPrice(listing) {
      return this.listingPricingContext(listing).recommended_price || ''
    },
    listingOfficialPrice(listing) {
      const context = this.listingPricingContext(listing)
      const officialTicket = listing && listing.official_ticket ? listing.official_ticket : {}
      return context.official_price || officialTicket.official_price || ''
    },
    listingPriceSourceLabel(listing) {
      return this.listingPricingContext(listing).recommended_price_source_label || 'Precio sugerido FOMO'
    },
    setListingEditSuggestedPrice(listing) {
      const suggestedPrice = this.listingSuggestedPrice(listing)
      if (suggestedPrice) this.setListingEditField(listing, 'unit_price', suggestedPrice)
    },
    isQrDeliveryMethod(method) {
      return ['qr_final_ticket', 'qr_redeem_physical'].includes(method)
    },
    proofStatusLabel(listing) {
      const labels = {
        missing: 'Sin evidencia',
        submitted: 'Evidencia guardada',
        pending_review: 'Guardada',
        approved: 'Guardada',
        rejected: 'Rechazada',
      }
      const status = listing && listing.proof_status
      return labels[status] || (status ? this.humanizeStatus(status) : 'Sin evidencia')
    },
    proofHelper(listing) {
      const proof = (listing && listing.proof_metadata) || {}
      return proof.note || proof.external_reference || 'Puedes guardar archivo, foto o referencia para respaldar esta entrada.'
    },
    listingLockedLabel(listing) {
      const reason = listing && listing.safe_actions ? String(listing.safe_actions.locked_reason || '') : ''
      if (reason.toLowerCase().includes('delivery') || reason.toLowerCase().includes('hold') || reason.toLowerCase().includes('venta')) {
        return 'Esta entrada ya tiene una venta o entrega en curso.'
      }
      return reason || 'Esta entrada no se puede editar ahora.'
    },
    deliveryItemTitle(delivery) {
      const item = delivery && delivery.item ? delivery.item : {}
      return item.listing_title || [item.section_label, item.row_label].filter(Boolean).join(' · ') || 'Entrada'
    },
    deliverySeatSummary(delivery) {
      const item = delivery && delivery.item ? delivery.item : {}
      return [item.section_label, item.row_label, item.seat_label].filter(Boolean).join(' · ') || 'Asiento pendiente'
    },
    canSubmitDeliveryHandoff(delivery) {
      return Boolean(delivery && !this.isQrDeliveryMethod(delivery.delivery_method) && delivery.order_payment_status === 'verified' && delivery.status === 'pending_seller_action')
    },
    canSubmitDeliveryHandoffForm(delivery) {
      if (!this.canSubmitDeliveryHandoff(delivery)) return false
      const form = this.deliveryHandoffForms[(delivery && delivery.id) || ''] || {}
      return Boolean(form.file || String(form.evidence_reference || '').trim() || String(form.note || '').trim())
    },
    deliveryLockLabel(delivery) {
      if (!delivery) return 'Entrega pendiente.'
      const state = swapDeliveryProgress(delivery, delivery.order_payment_status)
      return state.done ? `${state.label}. El comprador puede consultar la evidencia en su compra.` : state.label
    },
    syncDeliveryHandoffForms() {
      const forms = {}
      this.deliveries.forEach((delivery) => {
        const existing = this.deliveryHandoffForms[delivery.id] || {}
        const handoff = (delivery.metadata && delivery.metadata.seller_handoff) || {}
        forms[delivery.id] = {
          handoff_method: Object.prototype.hasOwnProperty.call(existing, 'handoff_method') ? existing.handoff_method : (handoff.handoff_method || delivery.delivery_method || 'physical_handoff'),
          evidence_reference: Object.prototype.hasOwnProperty.call(existing, 'evidence_reference') ? existing.evidence_reference : (handoff.evidence_reference || ''),
          note: Object.prototype.hasOwnProperty.call(existing, 'note') ? existing.note : (handoff.note || ''),
          file: Object.prototype.hasOwnProperty.call(existing, 'file') ? existing.file : null,
        }
      })
      this.deliveryHandoffForms = forms
    },
    syncListingForms() {
      const editForms = {}
      const proofForms = {}
      this.listings.forEach((listing) => {
        const existingEdit = this.listingEditForms[listing.id] || {}
        editForms[listing.id] = {
          title: Object.prototype.hasOwnProperty.call(existingEdit, 'title') ? existingEdit.title : (listing.title || ''),
          unit_price: Object.prototype.hasOwnProperty.call(existingEdit, 'unit_price') ? existingEdit.unit_price : (listing.unit_price || ''),
          delivery_method: Object.prototype.hasOwnProperty.call(existingEdit, 'delivery_method') ? existingEdit.delivery_method : (listing.delivery_method || 'qr_final_ticket'),
          fulfillment_mode: Object.prototype.hasOwnProperty.call(existingEdit, 'fulfillment_mode') ? existingEdit.fulfillment_mode : (listing.fulfillment_mode || (listing.metadata && listing.metadata.fulfillment_mode) || 'per_unit'),
          seat_labels_text: Object.prototype.hasOwnProperty.call(existingEdit, 'seat_labels_text') ? existingEdit.seat_labels_text : this.unitSeatText(listing),
          external_refs_text: Object.prototype.hasOwnProperty.call(existingEdit, 'external_refs_text') ? existingEdit.external_refs_text : this.unitExternalRefText(listing),
        }
        proofForms[listing.id] = {
          file: null,
          external_reference: ((listing.proof_metadata || {}).external_reference) || '',
          note: '',
        }
      })
      this.listingEditForms = editForms
      this.listingProofForms = proofForms
    },
    handoffField(delivery, field) {
      const form = this.deliveryHandoffForms[(delivery && delivery.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setHandoffField(delivery, field, value) {
      if (!delivery || !delivery.id) return
      this.deliveryHandoffForms = {
        ...this.deliveryHandoffForms,
        [delivery.id]: {
          ...(this.deliveryHandoffForms[delivery.id] || {}),
          [field]: value,
        },
      }
    },
    setHandoffFile(delivery, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setHandoffField(delivery, 'file', file)
    },
    syncProfileForm() {
      this.profileForm = {
        public_name: (this.profile && this.profile.public_name) || '',
        legal_name: (this.profile && this.profile.legal_name) || '',
        contact_phone: this.profile?.contact_phone || '',
        whatsapp_enabled: this.profile?.whatsapp_enabled || false,
        email_notifications_enabled: this.profile ? this.profile.email_notifications_enabled : true,
        terms_accepted: Boolean(this.profile && this.profile.terms_accepted_at),
      }
    },
    async loadAll(options = {}) {
      this.loading = true
      if (!options.preserveFeedback) this.feedback = ''
      const [profilePayload, eventsPayload, listingsPayload, deliveriesPayload, messagesPayload, financePayload] = await Promise.all([
        this.swapApi('/api/swap/seller/profile/'),
        this.swapApi('/api/swap/seller/events/'),
        this.swapApi('/api/swap/seller/listings/'),
        this.swapApi('/api/swap/seller/deliveries/'),
        this.swapApi('/api/swap/seller/messages/'),
        this.swapApi('/api/swap/seller/finance/'),
      ])
      if (!profilePayload || !Object.prototype.hasOwnProperty.call(profilePayload, 'profile')) {
        this.feedback = this.extractError(profilePayload, 'No pudimos cargar tu perfil Swap.')
        this.feedbackType = 'error'
      } else {
        this.sellerOwnerId = profilePayload?.user_id || profilePayload?.profile?.user || ''
        this.profile = profilePayload ? profilePayload.profile : null
        this.market = profilePayload ? profilePayload.market : null
        this.syncProfileForm()
      }
      if (Array.isArray(eventsPayload)) this.events = eventsPayload
      if (Array.isArray(listingsPayload)) this.listings = listingsPayload
      if (Array.isArray(deliveriesPayload)) this.deliveries = deliveriesPayload
      if (Array.isArray(messagesPayload?.conversations)) this.messageThreads = messagesPayload.conversations
      if (financePayload?.summary) this.finance = financePayload
      if (!Array.isArray(eventsPayload) || !Array.isArray(listingsPayload) || !Array.isArray(deliveriesPayload) || !Array.isArray(messagesPayload?.conversations) || !financePayload?.summary) {
        this.feedback = 'No pudimos cargar todos los datos. Usa Actualizar para reintentar.'
        this.feedbackType = 'error'
      }
      this.syncDeliveryHandoffForms()
      this.syncListingForms()
      if (!this.listingForm.event && this.events[0]) this.listingForm.event = this.events[0].id
      if (this.listingForm.event) await this.loadPricingOptions(this.listingForm.event)
      this.loading = false
    },
    async saveProfile() {
      if (this.savingProfile || !this.profileDirty) return
      this.savingProfile = true
      this.feedback = ''
      const data = await this.swapApi('/api/swap/seller/profile/', 'POST', this.profileForm)
      this.savingProfile = false
      if (data && data.id && !data.err) {
        this.profile = data
        this.syncProfileForm()
        this.feedback = 'Perfil guardado.'
        this.feedbackType = 'success'
        return
      }
      this.feedback = this.extractError(data, 'No pudimos guardar tu perfil.')
      this.feedbackType = 'error'
    },
    openSellSection() {
      if (this.embedded) this.setEmbeddedSection('sell')
      else this.$router.push({ name: 'swap-seller-sell' })
    },
    async createListing() {
      if (!this.canSubmitListing || this.savingListing) return
      this.savingListing = true
      this.feedback = ''
      const proofDraft = {
        file: this.listingForm.proof_file,
        external_reference: this.externalRefLines.join(' | '),
        note: this.listingForm.proof_note,
        delivery_method: this.listingForm.delivery_method,
      }
      const payload = {
        event: this.listingForm.event,
        official_ticket: this.listingForm.official_ticket,
        title: this.listingForm.title,
        section_label: this.listingForm.section_label,
        row_label: this.listingForm.row_label,
        quantity: Number(this.listingForm.quantity || 1),
        unit_price: this.listingForm.unit_price,
        currency: 'USD',
        delivery_method: this.listingForm.delivery_method,
        fulfillment_mode: this.listingForm.fulfillment_mode,
        seats_together: this.listingForm.seats_together,
        seat_labels: this.parseLines(this.listingForm.seat_labels_text),
        external_ticket_refs: this.listingExternalRefsForPayload(),
      }
      const data = await this.swapApi('/api/swap/seller/listings/', 'POST', payload)
      if (data && data.id && !data.err) {
        const proofError = await this.uploadListingDraftProof(data, proofDraft)
        this.savingListing = false
        this.feedback = proofError ? `Entrada publicada, pero no pudimos adjuntar la evidencia: ${proofError}` : 'Entrada publicada con evidencia lista.'
        this.feedbackType = proofError ? 'error' : 'success'
        const selectedEvent = this.listingForm.event
        this.listingForm = this.defaultListingForm()
        this.listingForm.event = selectedEvent
        this.saleStep = 'category'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.savingListing = false
      this.feedback = this.extractError(data, 'No pudimos publicar la entrada.')
      this.feedbackType = 'error'
    },
    async uploadListingDraftProof(listing, draft) {
      if (!listing || !listing.id || !draft) return ''
      const hasFile = Boolean(draft.file)
      const externalReference = String(draft.external_reference || '').trim()
      if (!hasFile && !externalReference) return ''
      const payload = new FormData()
      if (hasFile) payload.append('file', draft.file)
      payload.append('artifact_type', hasFile ? 'ticket_file' : 'transfer_reference')
      payload.append('external_reference', externalReference)
      payload.append('note', draft.note || this.deliveryMethodLabel(draft.delivery_method))
      const data = await this.swapApiForm(`/api/swap/seller/listings/${listing.id}/proof/`, 'POST', payload, { timeoutMs: 60000 })
      if (data && data.id && !data.err) return ''
      return this.extractError(data, 'evidencia rechazada')
    },
    listingEditField(listing, field) {
      const form = this.listingEditForms[(listing && listing.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setListingEditField(listing, field, value) {
      if (!listing || !listing.id) return
      this.listingEditForms = {
        ...this.listingEditForms,
        [listing.id]: {
          ...(this.listingEditForms[listing.id] || {}),
          [field]: value,
        },
      }
    },
    async saveListingEdit(listing) {
      if (!listing || !listing.id || this.savingListingEditKey) return
      const form = this.listingEditForms[listing.id] || {}
      const payload = {
        title: form.title || '',
        unit_price: form.unit_price || listing.unit_price,
        delivery_method: form.delivery_method || listing.delivery_method,
        fulfillment_mode: form.fulfillment_mode || listing.fulfillment_mode || 'per_unit',
      }
      if (String(form.seat_labels_text || '').trim()) payload.seat_labels = this.parseLines(form.seat_labels_text)
      if (String(form.external_refs_text || '').trim()) payload.external_ticket_refs = this.parseLines(form.external_refs_text)
      this.savingListingEditKey = `listing-${listing.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/listings/${listing.id}/`, 'PATCH', payload)
      this.savingListingEditKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Entrada actualizada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar la entrada.')
      this.feedbackType = 'error'
    },
    listingProofField(listing, field) {
      const form = this.listingProofForms[(listing && listing.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setListingProofField(listing, field, value) {
      if (!listing || !listing.id) return
      this.listingProofForms = {
        ...this.listingProofForms,
        [listing.id]: {
          ...(this.listingProofForms[listing.id] || {}),
          [field]: value,
        },
      }
    },
    setListingProofFile(listing, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setListingProofField(listing, 'file', file)
    },
    canUploadProof(listing) {
      const form = this.listingProofForms[(listing && listing.id) || ''] || {}
      return Boolean(form.file || String(form.external_reference || '').trim())
    },
    async uploadListingProof(listing) {
      if (!listing || !listing.id || this.uploadingProofKey || !this.canUploadProof(listing)) return
      const form = this.listingProofForms[listing.id] || {}
      const payload = new FormData()
      if (form.file) payload.append('file', form.file)
      if (listing.fulfillment_mode !== 'package') payload.append('unit_number', form.unit_number || 1)
      payload.append('artifact_type', form.file ? 'ticket_file' : 'transfer_reference')
      payload.append('external_reference', form.external_reference || '')
      payload.append('note', form.note || '')
      this.uploadingProofKey = `proof-${listing.id}`
      this.feedback = ''
      const data = await this.swapApiForm(`/api/swap/seller/listings/${listing.id}/proof/`, 'POST', payload, { timeoutMs: 60000 })
      this.uploadingProofKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Evidencia guardada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos subir la evidencia.')
      this.feedbackType = 'error'
    },
    async runListingAction(listing, action) {
      if (!listing || !listing.id || this.listingActionKey) return
      if (action === 'cancel' && typeof window !== 'undefined' && !window.confirm('Cancelar esta entrada? No se puede deshacer.')) return
      this.listingActionKey = `${action}-${listing.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/listings/${listing.id}/action/`, 'POST', {
        action,
        note: `Seller action ${action} desde dashboard Swap.`,
      })
      this.listingActionKey = ''
      if (data && data.id && !data.err) {
        this.feedback = action === 'pause' ? 'Entrada pausada.' : action === 'resume' ? 'Entrada reactivada.' : 'Entrada cancelada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos cambiar esta entrada.')
      this.feedbackType = 'error'
    },
    async submitDeliveryHandoff(delivery) {
      if (!this.canSubmitDeliveryHandoffForm(delivery) || this.savingDeliveryKey) return
      this.savingDeliveryKey = `delivery-${delivery.id}`
      this.feedback = ''
      const form = this.deliveryHandoffForms[delivery.id] || {}
      const payload = new FormData()
      payload.append('evidence_reference', form.evidence_reference || '')
      payload.append('handoff_method', form.handoff_method || 'physical_handoff')
      payload.append('note', form.note || '')
      if (form.file) payload.append('evidence_file', form.file)
      const data = await this.swapApiForm(`/api/swap/seller/deliveries/${delivery.id}/handoff/`, 'POST', payload, { timeoutMs: 60000 })
      this.savingDeliveryKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Entrega registrada. Tu comprador ya puede consultar la evidencia.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos enviar la entrega.')
      this.feedbackType = 'error'
    },
    messageDraft(threadId) {
      return this.messageDrafts[threadId] || ''
    },
    setMessageDraft(threadId, value) {
      this.messageDrafts = { ...this.messageDrafts, [threadId]: value }
    },
    async sendSellerMessage(thread) {
      if (!thread || !thread.id || this.sendingMessageKey) return
      const body = String(this.messageDraft(thread.id) || '').trim()
      if (!body) return
      this.sendingMessageKey = `thread-${thread.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/messages/${thread.id}/`, 'POST', { body })
      this.sendingMessageKey = ''
      if (data && data.id && !data.err) {
        this.setMessageDraft(thread.id, '')
        this.feedback = 'Mensaje enviado a FOMO.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos enviar tu mensaje.')
      this.feedbackType = 'error'
    },
  },
}
</script>

<style scoped>
.workspace-v2 {
  position: relative;
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at top left, rgba(176, 185, 204, 0.08), transparent 24%),
    radial-gradient(circle at 82% 12%, rgba(76, 60, 94, 0.14), transparent 22%),
    linear-gradient(180deg, #06060b, #0a0910 42%, #0d0b14 100%);
}
.workspace-v2::before,
.workspace-v2::after { content: ''; position: fixed; pointer-events: none; z-index: 0; filter: blur(24px); }
.workspace-v2::before { top: 120px; right: 10%; width: 220px; height: 220px; border-radius: 999px; background: radial-gradient(circle, rgba(178, 188, 208, 0.14), rgba(178, 188, 208, 0)); }
.workspace-v2::after { bottom: 16%; left: 8%; width: 280px; height: 280px; border-radius: 999px; background: radial-gradient(circle, rgba(80, 62, 96, 0.14), rgba(80, 62, 96, 0)); }
.seller-swap-page { align-content: start; }
.seller-swap-page--embedded { min-height: 0; padding: 0; background: transparent; }
.seller-swap-page--embedded::before,
.seller-swap-page--embedded::after { display: none; }
.workspace-v2__shell { position: relative; z-index: 1; display: flex; align-items: flex-start; gap: 20px; max-width: 1440px; margin: 0 auto; }
.seller-swap-page--embedded .workspace-v2__shell { max-width: none; }
.workspace-v2__nav--mobile { display: none; }
.workspace-v2__nav--desktop { display: block; flex: 0 0 296px; width: 296px; align-self: flex-start; }
.workspace-v2__main { flex: 1 1 auto; min-width: 0; display: grid; gap: 18px; }
@media (min-width: 981px) {
  .workspace-v2__shell { overflow: visible; }
  .workspace-v2__shell > :deep(.organizer-nav) { position: static !important; inset: auto !important; top: auto !important; bottom: auto !important; left: auto !important; right: auto !important; align-self: start !important; height: auto !important; max-height: none !important; overflow: visible !important; transform: none !important; }
  .workspace-v2__shell > :deep(.organizer-nav__panel) { position: static !important; inset: auto !important; top: auto !important; bottom: auto !important; left: auto !important; right: auto !important; height: auto !important; max-height: none !important; overflow: visible !important; transform: none !important; }
}
.workspace-v2__kicker { color: rgba(201, 214, 230, 0.68); font-size: 0.68rem; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; }
.seller-swap-embedded-menu { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; padding: 10px; border-radius: 28px; background: rgba(255, 255, 255, 0.035); border: 1px solid rgba(198, 208, 230, 0.1); }
.seller-swap-embedded-menu__item { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 10px; align-items: center; min-height: 66px; padding: 12px 14px; border: 0; border-radius: 20px; color: rgba(232, 238, 248, 0.74); background: transparent; text-align: left; font-weight: 900; cursor: pointer; transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease; }
.seller-swap-embedded-menu__item i { grid-row: span 2; color: rgba(201, 214, 230, 0.72); }
.seller-swap-embedded-menu__item small { color: rgba(201, 214, 230, 0.56); font-size: 0.68rem; font-weight: 800; }
.seller-swap-embedded-menu__item.is-active,
.seller-swap-embedded-menu__item:hover { color: #fff; background: rgba(255, 255, 255, 0.08); transform: translateY(-1px); }
.seller-swap-hero, .seller-swap-panel { padding: 24px; }
.seller-swap-hero { display: grid; grid-template-columns: minmax(0,1fr) auto; align-items:center; gap:24px; padding:24px; }
.seller-swap-page h1, .seller-swap-page h2, .seller-swap-page h3 { margin:0; color:var(--fomo-dashboard-text); -webkit-text-fill-color:currentColor; text-shadow:none; background:none; font-family:var(--fomo-font-heading); text-align:left; }
.seller-swap-panel h2 { font-size:24px; line-height:1.2; letter-spacing:-.04em; }
.seller-swap-hero p, .seller-swap-panel p { margin: 0; color:var(--fomo-dashboard-copy); font-family:var(--fomo-font-body); line-height:1.6; }
.seller-swap-hero__bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.seller-swap-section-chip { display: grid; gap: 2px; min-width: 172px; padding: 10px 12px; border-radius: 16px; background: rgba(255,255,255,0.045); border: 1px solid rgba(198, 208, 230, 0.1); }
.seller-swap-section-chip small { color: rgba(214, 223, 240, 0.54); font-family: 'Space Grotesk', sans-serif; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; }
.seller-swap-section-chip strong { color: #f4f8ff; font-family: 'Syne', sans-serif; font-size: 0.98rem; letter-spacing: -0.04em; }
.seller-swap-hero__actions { display: inline-flex; align-items: center; justify-content: flex-end; gap: 10px; min-width: 0; }
.seller-swap-hero__main { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; align-items: stretch; }
.seller-swap-hero__copy { display: grid; align-content: center; gap: 7px; min-width: 0; }
.seller-swap-hero__copy h1 { max-width:820px; font-family:var(--fomo-font-heading); font-size:clamp(26px,2.5vw,36px); line-height:1.15; letter-spacing:-.05em; }
.seller-swap-hero__copy p { max-width: 780px; font-size: 0.95rem; line-height: 1.45; }
.seller-swap-panel { display: grid; gap: 18px; }
.seller-swap-price-context { display: grid; gap: 4px; padding: 13px; border-radius: 18px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-price-context span { color: rgba(221, 231, 246, 0.66); font-size: 0.74rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; }
.seller-swap-price-context strong { color: #f8fbff; }
.seller-swap-form label small { color: rgba(221, 231, 246, 0.66); font-size: 0.78rem; font-weight: 700; line-height: 1.35; }
.seller-swap-price-context em { color: rgba(221, 231, 246, 0.72); font-style: normal; }
.seller-swap-price-context { grid-template-columns: repeat(3, minmax(0, 1fr)) auto; align-items: center; }
.seller-swap-panel__header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.seller-swap-panel__header--stacked { align-items: center; }
.seller-swap-panel__header--compact { align-items: center; }
.seller-swap-form { display: grid; gap: 12px; }
.seller-swap-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-field--wide { grid-column: 1 / -1; }
.seller-swap-form input, .seller-swap-form select, .seller-swap-form textarea { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-form textarea { resize: vertical; }
.seller-swap-form :is(input,select,textarea):focus-visible { outline:2px solid var(--fomo-dashboard-label); outline-offset:3px; }
.seller-swap-check { display: flex !important; grid-template-columns: auto 1fr; align-items: center; }
.seller-swap-check input { width: auto; }
.seller-swap-pill { display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; padding: 7px 10px; font-size: 0.78rem; font-weight: 900; text-transform: uppercase; background: rgba(255,255,255,0.08); color: rgba(236, 243, 255, 0.8); }
.seller-swap-pill.is-active { background: rgba(52, 211, 153, 0.16); color: #95f5d0; }
.seller-swap-pill.is-warning { background:rgba(92,71,130,.22);color:var(--fomo-dashboard-label); }
.seller-swap-pill.is-danger { background: rgba(248, 113, 113, 0.17); color: #ffc0c0; }
.seller-swap-pill.is-muted { background: rgba(148, 163, 184, 0.16); color: #d7e1ef; }
.seller-swap-feedback { padding: 13px 16px; border-radius: 18px; font-weight: 800; }
.seller-swap-feedback.is-success { background: rgba(52, 211, 153, 0.14); color: #b9ffe4; border: 1px solid rgba(52, 211, 153, 0.22); }
.seller-swap-feedback.is-error { background: rgba(248, 113, 113, 0.16); color: #ffd2d2; border: 1px solid rgba(248, 113, 113, 0.24); }
.seller-swap-empty { padding: 22px; border-radius: 20px; background: rgba(255,255,255,0.04); color: rgba(221, 231, 246, 0.76); }
.seller-swap-entry-filters { display: flex; flex-wrap: wrap; gap: 8px; }
.seller-swap-entry-filter { display: inline-flex; align-items: center; gap: 8px; min-height: 38px; padding: 0 12px; border: 1px solid rgba(188, 207, 255, 0.12); border-radius: 999px; color: rgba(229, 237, 250, 0.72); background: rgba(255,255,255,0.045); font-weight: 900; cursor: pointer; }
.seller-swap-entry-filter strong { display:inline-grid;place-items:center;min-width:24px;height:24px;border-radius:999px;color:var(--fomo-dashboard-label);background:rgba(196,206,228,.08);font-size:.76rem; }
.seller-swap-entry-filter.is-active { color:var(--fomo-dashboard-text);border-color:rgba(196,206,228,.35);background:var(--fomo-control); }
.seller-swap-entry-filter:focus-visible { outline:2px solid var(--fomo-dashboard-label);outline-offset:2px; }
.seller-swap-entry-table { display: grid; gap: 10px; }
.seller-swap-entry-row { display: grid; gap: 0; overflow: hidden; }
.seller-swap-entry-row__main { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(210px, 0.8fr) minmax(150px, 0.45fr) minmax(220px, 0.6fr); gap: 14px; align-items: center; padding: 14px; }
.seller-swap-entry-row__entry, .seller-swap-entry-row__money { display: grid; gap: 4px; min-width: 0; }
.seller-swap-entry-row__entry small, .seller-swap-entry-row__money small { color: rgba(221, 231, 246, 0.72); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-entry-row__entry strong, .seller-swap-entry-row__money strong { color: #f8fbff; overflow-wrap: anywhere; }
.seller-swap-entry-row__entry span, .seller-swap-entry-row__entry em, .seller-swap-entry-row__money span { color: rgba(221, 231, 246, 0.7); font-size: 0.84rem; font-style: normal; overflow-wrap: anywhere; }
.seller-swap-entry-row__status, .seller-swap-entry-row__actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.seller-swap-entry-row__actions { justify-content: flex-end; }
.seller-swap-entry-row__details { display: grid; gap: 12px; padding: 14px; border-top: 1px solid rgba(188, 207, 255, 0.1); background: rgba(0, 0, 0, 0.12); }
.seller-swap-finance-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.seller-swap-finance-card { display: grid; gap: 8px; min-height: 132px; padding: 16px; }
.seller-swap-finance-card small { color: rgba(221, 231, 246, 0.72); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-finance-card strong { color: #f8fbff; font-size: 1.35rem; }
.seller-swap-finance-card span { color: rgba(221, 231, 246, 0.68); font-size: 0.82rem; }
.seller-swap-finance-split { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); gap: 14px; }
.seller-swap-finance-list { display: grid; gap: 12px; padding: 16px; }
.seller-swap-row-list { display: grid; gap: 10px; }
.seller-swap-row-card { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; padding: 13px; }
.seller-swap-row-card div { display: grid; gap: 4px; min-width: 0; }
.seller-swap-row-card div:last-child { justify-items: flex-end; }
.seller-swap-row-card strong { color: #f8fbff; }
.seller-swap-row-card span, .seller-swap-row-card small { color: rgba(221, 231, 246, 0.7); overflow-wrap: anywhere; }
.seller-swap-artifact-list { display: grid; gap: 8px; }
.seller-swap-artifact-list article { display: grid; gap: 4px; padding: 11px; border-radius: 16px; background: rgba(255,255,255,0.055); border: 1px solid rgba(120, 240, 202, 0.14); }
.seller-swap-artifact-list small { color: rgba(120, 240, 202, 0.76); font-size: 0.64rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.seller-swap-artifact-list strong { color: #f8fbff; }
.seller-swap-artifact-list span { color: rgba(221, 231, 246, 0.72); font-size: 0.84rem; overflow-wrap: anywhere; }
.seller-swap-artifact-list a { color: #95f5d0; font-weight: 900; overflow-wrap: anywhere; }
.seller-swap-proof-strip { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; padding: 12px; }
.seller-swap-proof-strip div { display: grid; gap: 3px; min-width: 0; }
.seller-swap-proof-strip small { color: rgba(221, 231, 246, 0.72); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-proof-strip strong { color: #f8fbff; }
.seller-swap-proof-strip span { color: rgba(221, 231, 246, 0.7); font-size: 0.84rem; overflow-wrap: anywhere; }
.seller-swap-proof-strip a { color: #95f5d0; font-weight: 900; white-space: nowrap; }
.seller-swap-listing-edit, .seller-swap-proof-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding-top: 4px; }
.seller-swap-listing-edit label, .seller-swap-proof-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-listing-edit input, .seller-swap-listing-edit select, .seller-swap-listing-edit textarea, .seller-swap-proof-form input { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-listing-edit textarea { resize: vertical; }
.seller-swap-actions-row { display: flex; flex-wrap: wrap; gap: 8px; }
.seller-swap-delivery-form { display: grid; gap: 10px; padding-top: 4px; }
.seller-swap-delivery-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-delivery-form input { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-delivery-lock { border-radius: 16px; padding: 12px 13px; background: rgba(255,255,255,0.05); color: rgba(221, 231, 246, 0.72); font-weight: 700; }
.seller-swap-message-compose { display: grid; gap: 10px; }
.seller-swap-message-compose label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-message-compose--inline { padding: 12px; border-radius: 18px; background: rgba(109, 168, 255, 0.08); border: 1px solid rgba(109, 168, 255, 0.14); }
.seller-swap-message-compose textarea { width: 100%; color: #f8fbff; padding: 12px 13px; outline: none; resize: vertical; }
.seller-swap-profile-layout {display:grid;grid-template-columns:minmax(0,1.65fr) minmax(260px,.85fr);gap:24px;align-items:start}
.seller-swap-profile-form {display:grid;gap:24px;min-width:0}
.seller-swap-profile-fields {border:0;margin:0;padding:0;min-width:0}
.seller-swap-profile-fields__grid {grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
.seller-swap-profile-panel .seller-swap-pill {justify-self:start}
.seller-swap-profile-fields legend {font-family:var(--fomo-font-label);font-size:12px;font-weight:600;color:var(--fomo-dashboard-label);margin-bottom:14px;padding:0}
.seller-swap-profile-fields label {font-family:var(--fomo-font-body);color:var(--fomo-dashboard-label);font-size:13px;font-weight:500;align-content:start}
.seller-swap-profile-fields label :is(span,small),.seller-swap-profile-email :is(span,strong,small),.seller-swap-profile-person>div :is(span,strong),.seller-swap-profile-contact-preview :is(span,strong,small),.seller-swap-profile-terms :is(label,span) {font-family:inherit}
.seller-swap-profile-fields input:not([type=checkbox]) {min-height:46px;font-family:var(--fomo-font-body);font-size:14px;border-radius:12px;min-width:0;color:var(--fomo-dashboard-text);background:rgba(196,206,228,.045);padding:12px}
.seller-swap-profile-fields label small,.seller-swap-profile-email small {font-size:12px;line-height:1.6;font-weight:400;color:var(--fomo-dashboard-copy)}
.seller-swap-profile-email {display:grid;align-content:start;gap:8px;min-width:0;font-family:var(--fomo-font-body);font-size:13px;color:var(--fomo-dashboard-label)}
.seller-swap-profile-email strong {font-size:14px;line-height:1.5;overflow-wrap:anywhere;color:var(--fomo-dashboard-text);font-weight:500;padding-block:10px}
.seller-swap-profile-optin {grid-column:1/-1;padding:14px;border:1px solid var(--fomo-border);border-radius:12px;gap:12px;line-height:1.6}
.seller-swap-profile-optin input {flex:0 0 auto;width:18px;height:18px;accent-color:var(--fomo-dashboard-label)}
.seller-swap-profile-terms {border-top:1px solid var(--fomo-border);padding-top:18px;font-family:var(--fomo-font-body);font-size:12px;color:var(--fomo-dashboard-copy)}
.seller-swap-profile-terms p {display:flex;align-items:center;gap:8px}
.seller-swap-profile-footer {display:flex;align-items:center;justify-content:flex-end;flex-wrap:wrap;gap:12px;border-top:1px solid var(--fomo-border);padding-top:20px}
.seller-swap-profile-footer>span {margin-right:auto;font-size:11px;color:var(--fomo-dashboard-copy);font-family:var(--fomo-font-body)}
.seller-swap-profile-footer>.fomo-button {width:auto;flex:0 1 auto}
.seller-swap-profile-preview {display:grid;gap:24px;padding:24px;font-family:var(--fomo-font-body);min-width:0}
.seller-swap-profile-preview h2 {font-size:22px;line-height:1.2;letter-spacing:-.04em;margin-top:-14px}
.seller-swap-profile-person {display:flex;align-items:center;gap:12px;min-width:0}
.seller-swap-profile-avatar {display:grid;place-items:center;flex:0 0 48px;height:48px;border-radius:50%;border:1px solid var(--fomo-border);background:var(--fomo-control);font-family:var(--fomo-font-heading);font-size:18px;color:var(--fomo-dashboard-label)}
.seller-swap-profile-person>div {display:grid;gap:6px;min-width:0}
.seller-swap-profile-person strong {font-size:16px;color:var(--fomo-dashboard-text);overflow-wrap:anywhere}
.seller-swap-profile-person span:not(.seller-swap-profile-avatar) {font-size:11px;color:var(--fomo-dashboard-copy)}
.seller-swap-profile-contact-preview {display:grid;gap:10px;padding-block:20px;border-block:1px dashed var(--fomo-border);overflow-wrap:anywhere}
.seller-swap-profile-contact-preview span,.seller-swap-profile-contact-preview small {font-size:11px;color:var(--fomo-dashboard-copy)}
.seller-swap-profile-contact-preview strong {font-size:13px;font-weight:500;color:var(--fomo-dashboard-label)}
.seller-swap-profile-protection {display:grid;gap:12px;color:var(--fomo-dashboard-label)}
.seller-swap-profile-protection h3 {font-family:var(--fomo-font-body);font-size:13px;line-height:1.5;font-weight:600;margin-bottom:8px}
.seller-swap-profile-protection p {font-size:12px;line-height:1.7;color:var(--fomo-dashboard-copy);margin:0}
.seller-swap-empty--illustrated {display:grid;justify-items:center;text-align:center;gap:16px;padding:40px 24px;background:transparent}
.seller-swap-empty--illustrated svg {color:var(--fomo-dashboard-label)}
.seller-swap-empty--illustrated h3 {font-size:22px;text-align:center}
.seller-swap-empty--illustrated p {max-width:400px;font-size:13px}
@media (max-width: 1180px) {
  .seller-swap-entry-row__main { grid-template-columns: minmax(0, 1fr) minmax(190px, 0.7fr); }
  .seller-swap-entry-row__actions { justify-content: flex-start; }
  .seller-swap-finance-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .seller-swap-finance-split { grid-template-columns: 1fr; }
}
@media (max-width: 980px) {
  .workspace-v2 { padding: 12px; }
  .workspace-v2__nav--mobile { display: block; }
  .workspace-v2__nav--desktop { display: none; }
  .workspace-v2__shell { display: grid; grid-template-columns: 1fr; }
  .seller-swap-hero { padding: 14px; }
  .seller-swap-profile-layout {grid-template-columns:1fr}
  .seller-swap-embedded-menu { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .seller-swap-hero__main, .seller-swap-listing-edit, .seller-swap-proof-form { grid-template-columns: 1fr; }
  .seller-swap-hero__bar { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; }
  .seller-swap-section-chip { min-width: 0; }
  .seller-swap-hero__actions { justify-content: flex-end; width: auto; }
  .seller-swap-hero__copy h1 { font-size: 1.42rem; line-height: 1.04; }
  .seller-swap-hero__copy p { display: -webkit-box; overflow: hidden; font-size: 0.84rem; line-height: 1.35; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
  .seller-swap-price-context { grid-template-columns: 1fr; }
  .seller-swap-panel__header, .seller-swap-panel__header--stacked { display: grid; }
  .seller-swap-entry-filters { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 2px; }
  .seller-swap-entry-filter { flex: 0 0 auto; }
  .seller-swap-entry-row__main { grid-template-columns: 1fr; gap: 10px; }
  .seller-swap-entry-row__actions { justify-content: flex-start; }
  .seller-swap-proof-strip { flex-direction: column; }
  .seller-swap-finance-grid { grid-template-columns: 1fr; }
  .seller-swap-row-card { display: grid; }
  .seller-swap-row-card div:last-child { justify-items: start; }
}
@media (max-width: 640px) {
  .seller-swap-hero {grid-template-columns:1fr;gap:16px;padding:20px;justify-items:start}
  .seller-swap-profile-fields__grid {grid-template-columns:1fr}
  .seller-swap-profile-panel,.seller-swap-profile-preview {padding:20px}
  .seller-swap-profile-footer {justify-content:flex-start;gap:10px}
  .seller-swap-profile-footer>span {flex-basis:100%}
  .seller-swap-profile-footer>.fomo-button:last-child {flex-basis:100%}
  .seller-swap-hero__bar { grid-template-columns: 1fr; }
  .seller-swap-hero__actions { justify-content: stretch; }
  .seller-swap-embedded-menu { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .seller-swap-embedded-menu__item { min-height: 58px; padding: 10px 12px; }
}
</style>
